import { useEffect, useState } from 'react';
import { DefaultValues, UseFormReturn } from 'react-hook-form';
import { DataListItem, Form } from '@brighte/form-components';
import { ApplicationInputType } from '@brighte/validation';
import { datadogRum } from '@datadog/browser-rum';
import { useStepperActions, useStepperActiveIndex } from '@provider';
import { Box } from '@spark-web/box';
import { Button } from '@spark-web/button';
import { Columns } from '@spark-web/columns';
import { ContentDialog } from '@spark-web/modal-dialog';
import { Stack } from '@spark-web/stack';
import { Text } from '@spark-web/text';
import { useDisclosure } from '@spark-web/utils';
import { Step } from '@spark-web/vertical-stepper';
import { APPLICATION_STEPS, KNOWN_ERROR_MESSAGES, getErrorMessage } from '@utils';
import { Loader } from '../Loader/Loader';
import ScrollIntoView from '../ScrollStepIntoView/ScrollStepIntoView';
import StepSummary from './StepSummary';
import { useSaveStepDetails } from './hooks';

type Props<TForm extends ApplicationInputType> = {
  children: (formProps: UseFormReturn<TForm>) => React.ReactNode;
  schema: any;
  name: string;
  title: string;
  isLoading?: boolean;
  defaultValues: DefaultValues<TForm>;
  isEditable?: boolean;
  completedContent?: Array<{ title: string; items: DataListItem[] | [] }> | [];
  disableNavigation?: boolean;
  shouldUnregister?: boolean;
  hideSubmitButton?: boolean;
  hideStepNumber?: boolean;
  description?: string;
  continueLabel?: string;
  showFormSummary?: boolean;
  footerContent?: (formProps: UseFormReturn<TForm>) => React.ReactNode;
  onBeforeNextStep?: (formValues: ApplicationInputType) => boolean;
};

function StepNavigator<TForm extends ApplicationInputType>({
  children,
  schema,
  name,
  title,
  isLoading = false,
  isEditable = true,
  defaultValues,
  completedContent,
  disableNavigation = false,
  shouldUnregister = true,
  hideSubmitButton = false,
  hideStepNumber = false,
  description,
  continueLabel = 'Save and Continue',
  showFormSummary = false,
  footerContent,
  onBeforeNextStep,
}: Props<TForm>) {
  const { onEdit, onNext } = useStepperActions();
  const activeIndex = useStepperActiveIndex();
  const [loading, setLoading] = useState(isLoading);
  const { isOpen, onOpen, onToggle } = useDisclosure();
  const [selectedStepIndex, setSelectedStepIndex] = useState<number>(0);
  const { saveStep } = useSaveStepDetails();
  const [error, setError] = useState<string | null>(null);

  const edit = (stepIndex: number) => {
    setSelectedStepIndex(stepIndex);
    onOpen();
  };

  const handleSubmit = async (values: ApplicationInputType) => {
    setLoading(true);
    try {
      datadogRum.addAction('Before calling save step', {
        name,
        values,
      });
      const { isStepSaved, message } = await saveStep({ [name]: values });
      setLoading(false);

      // if onBeforeNextStep returns true, it means the user wants to stop the navigation and trigger callback
      if (onBeforeNextStep && onBeforeNextStep(values)) {
        return;
      }

      if (isStepSaved) {
        // If the current step is the finance purchase, always navigate to the next step(Eligibility)
        if (name === APPLICATION_STEPS.FINANCE_PURCHASE) {
          datadogRum.addAction('Submitted finance step');
          onNext(activeIndex + 1);
        } else {
          datadogRum.addAction(`Submitted ${name} step`);
          onNext();
        }
        setError(null);
      } else {
        const actionName = message
          ? `Failed to save ${name} step`
          : `Failed to save ${name} step - missing application payload`;
        datadogRum.addAction(actionName, { message: message ?? 'Failed to save step' });
        setError(KNOWN_ERROR_MESSAGES.UPDATE_APPLICATION_ERROR);
        setLoading(false);
      }
    } catch (error) {
      const errorMessage = typeof error === 'string' ? error : getErrorMessage(error);
      datadogRum.addAction(`Failed to save ${name} step`, { message: errorMessage });
      setError(errorMessage);
      setLoading(false);
    }
  };

  useEffect(() => {
    setLoading(isLoading);
  }, [isLoading]);

  return (
    <>
      <Step>
        <Step.Heading
          title={title}
          renderActionButton={({ isCompleted, stepIndex }) => (
            <>
              <ScrollIntoView enabled={stepIndex === activeIndex} />
              {isCompleted && isEditable && (
                <Button type="button" onClick={() => edit(stepIndex)} prominence="none" tone="primary">
                  Edit
                </Button>
              )}
            </>
          )}
          renderStepNumber={hideStepNumber ? () => '' : undefined}
        />

        {description && (
          <Step.Description>
            <Box paddingBottom="small">
              <Text tone="muted" size="small">
                {description}
              </Text>
            </Box>
          </Step.Description>
        )}
        <Step.Content>
          {isLoading ? <Loader /> : null}

          {/* displays form summary with continue button */}
          {!isLoading && showFormSummary ? (
            <Stack gap="xsmall" paddingBottom="xlarge">
              <StepSummary completedContent={completedContent} />
              <Button disabled={disableNavigation} onClick={() => onNext()}>
                {continueLabel}
              </Button>
            </Stack>
          ) : null}

          {/* displays form */}
          {!isLoading && !showFormSummary ? (
            <Stack>
              <Form<TForm>
                onSubmit={handleSubmit}
                schema={schema}
                defaultValues={defaultValues}
                options={{ shouldUnregister }}
              >
                {({ ...formProps }: UseFormReturn<TForm>) => {
                  return (
                    <Stack gap="xxlarge" paddingBottom="xlarge">
                      {children(formProps)}
                      {error && <Text tone="critical">{error}</Text>}
                      {!hideSubmitButton && (
                        <Button type="submit" disabled={disableNavigation} loading={loading}>
                          {continueLabel}
                        </Button>
                      )}
                      {footerContent ? <Box>{footerContent(formProps)}</Box> : null}
                    </Stack>
                  );
                }}
              </Form>
            </Stack>
          ) : null}
        </Step.Content>

        <Step.CompletedContent>
          <StepSummary completedContent={completedContent} />
        </Step.CompletedContent>
      </Step>
      <ContentDialog title="Unsaved changes" isOpen={isOpen} onToggle={onToggle}>
        <Text>Are you sure you want to leave this section? Your changes will be lost if you proceed.</Text>
        <Columns gap="xxlarge">
          <Button
            onClick={() => {
              onToggle();
              onEdit(selectedStepIndex);
            }}
          >
            Yes, proceed
          </Button>
          <Button prominence="low" onClick={onToggle}>
            Cancel
          </Button>
        </Columns>
      </ContentDialog>
    </>
  );
}

export { StepNavigator };
